import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { getExhibitionSettings, getProfile } from '@app/app.reducer';
import { BaseRoute } from '@core/data-access/models/routes.model';
import { ProductBookingLoginGuard } from '@core/data-access/services/booking/product-booking-login-guard';
import { Store } from '@ngrx/store';
import { StatusBarService } from '@shared/status-bar/status-bar.service';
import { environment } from '@src/environments/environment';
import { SetSpinnerValue } from '@store/helpers/helper.actions';
import { HelperService } from '@store/helpers/helper.service';
import { GetProfile, LogIn, SetLoginType, SetUser } from '@store/user/user.actions';
import { LoginModel, LoginType } from '@store/user/user.interface';
import { includes, isEmpty, isNil } from 'lodash';
import { Observable, Subject, combineLatest, filter, first, map } from 'rxjs';
import {
  AccountVerificationStatus,
  LoginFormFields,
  LoginOptions,
  SocialLoginQueryParams,
  SocialLoginQueryParamsFirstLogin,
  SocialLoginQueryParamsLoginType
} from './login.model';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly helperService = inject(HelperService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly statusBarService = inject(StatusBarService);
  private readonly productBookingLoginGuard = inject(ProductBookingLoginGuard);

  private isSelectExhibitionPage$ = this.helperService.isSelectExhibitionPage$();

  activeTab$: Subject<'login' | 'registration'> = new Subject();
  profile$ = this.store.select(getProfile);
  exhibitionSettings$ = this.store.select(getExhibitionSettings);

  setActiveTab(tab: 'login' | 'registration') {
    this.activeTab$.next(tab);
  }

  checkAccountVerificationStatus$(): Observable<AccountVerificationStatus> {
    return combineLatest({
      profile: this.profile$,
      exhibitionSettings: this.exhibitionSettings$
    }).pipe(
      filter(({ exhibitionSettings }) => !isEmpty(exhibitionSettings)),
      map(({ exhibitionSettings, profile }) => ({
        isVerifiedAccountRequired:
          exhibitionSettings.loginMode === LoginOptions.BeforeTicketSelectionWithRequiredEmailConfirmation,
        isAccountVerified: profile ? profile.isEmailVerified : false
      })),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  submitLogin(loginFormFields: LoginFormFields) {
    const { password } = loginFormFields;
    let { email } = loginFormFields;
    email = email.toLowerCase();

    const credentials: LoginModel = { email, password };

    this.store.dispatch(new SetSpinnerValue(true));
    this.store.dispatch(new LogIn(credentials));
    this.store.dispatch(new SetLoginType(LoginType.Registration));

    this.redirectToSelectedStepAfterLogin();
  }

  socialNetworkLogin(socialNetwork: string) {
    const returnUrl = window.location.origin + this.router.url;
    this.redirectToLoginProvider(
      `${environment.protocol}${environment.webApiUrl}/user/external-login?provider=${socialNetwork}&returnUrl=${returnUrl}`
    );
  }

  redirectToLoginProvider(url: string) {
    window.open(url, '_self');
  }

  socialNetworkLoginQueryParamsSubscription() {
    return combineLatest({
      queryParams: this.activatedRoute.queryParams as Observable<SocialLoginQueryParams>,
      isSelectExhibitionPage: this.isSelectExhibitionPage$
    })
      .pipe(
        first(
          ({ queryParams: { type, error }, isSelectExhibitionPage }) =>
            (includes(
              [
                SocialLoginQueryParamsLoginType.LinkedIn,
                SocialLoginQueryParamsLoginType.Google,
                SocialLoginQueryParamsLoginType.Microsoft,
                SocialLoginQueryParamsLoginType.Apple,
                SocialLoginQueryParamsLoginType.Amazon,
                SocialLoginQueryParamsLoginType.Facebook,
                SocialLoginQueryParamsLoginType.X,
                SocialLoginQueryParamsLoginType.Vkontakte
              ],
              type
            ) ||
              (type === SocialLoginQueryParamsLoginType.SocialLoginError && !isNil(error))) &&
            !isSelectExhibitionPage
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ queryParams }) => {
        const { voucher, userId, authToken, firstLogin, error } = queryParams;

        const isLoginError = !isNil(error);
        const removeQueryParams = isEmpty(voucher) || isLoginError;

        if (removeQueryParams) {
          this.router.navigate([], { queryParams: {} });
        }

        if (isLoginError) {
          this.statusBarService.setStatus(error, 'error');
        }

        if (!isNil(userId) && !isNil(authToken)) {
          const isFirstLogin = firstLogin === SocialLoginQueryParamsFirstLogin.True;
          this.loginAndRedirect(+userId, authToken, isFirstLogin, LoginType.SocialNetwork);
        }
      });
  }

  loginAndRedirect(id: number, authToken: string, isFirstLogin: boolean, loginType = LoginType.SocialNetwork) {
    if (!isNil(id) && !isNil(authToken)) {
      this.store.dispatch(new GetProfile(id));
      this.store.dispatch(new SetUser({ id, authToken }));
      this.store.dispatch(new SetLoginType(loginType));

      if (!isNil(isFirstLogin) && isFirstLogin) {
        this.redirectToProfileAfterLogin();
      } else {
        this.redirectToSelectedStepAfterLogin();
      }

      this.productBookingLoginGuard.loggedIn();
    }
  }

  private redirectToProfileAfterLogin() {
    this.getProfile$().subscribe(() => {
      this.router.navigate([`/${BaseRoute.Profile}`]);
    });
  }

  private redirectToSelectedStepAfterLogin() {
    this.getProfile$().subscribe(() => {
      this.productBookingLoginGuard.loggedIn();

      this.helperService.redirectAfterLogin();
    });
  }

  private getProfile$() {
    return this.profile$.pipe(
      first((profile) => !isEmpty(profile)),
      takeUntilDestroyed(this.destroyRef)
    );
  }
}
